/*
 * improvemint-scroller.js — `<im-option-scroller>`, the theme's ONE horizontal
 * overflow scroller.
 *
 * WHY IT LIVES HERE AND NOT IN improvemint-variant-picker.js. It was written on
 * 2026-09-25 for the PDP's variant cards — GP: "lets make variants cards scroll
 * horizontally, and if overflows show some arrows to indicate that there are more
 * variants" — and shipped inside that file because it had exactly one caller. It
 * has two now: the vendor landing page's category bar
 * (sections/improvemint-vendor-nav.liquid) asked for the same behaviour the same
 * week, and a second copy of "reveal the arrow only while there is somewhere to
 * scroll" is precisely the kind of duplicate that drifts. Moved out whole on
 * 2026-09-25 — the class below is byte-for-byte the one that was in the picker,
 * and nothing about the PDP's behaviour changes.
 *
 * The element is loaded from layout/theme.liquid, so both callers get it without
 * either of them owning it. The picker does not load it (it duck-types `bring`
 * and works with this file blocked), and neither does the nav bar.
 *
 * WHAT IT IS, AND DELIBERATELY IS NOT. The track is a plain overflow container,
 * so touch, trackpad and mouse wheel all work with no JS at all, and a radio
 * group inside keeps its native arrow-key behaviour. This file only:
 *   - reveals each arrow while there is something to scroll to on that side,
 *   - pages the track on an arrow click,
 *   - keeps a focused INPUT's card in view (see `onFocusIn`),
 *   - brings the checked card into view after a re-render (`revealChecked`).
 * Motion is CSS's decision — `scroll-behavior` under a prefers-reduced-motion
 * query in component-improvemint-scroller.css — never a `behavior:` option here,
 * so there is one switch rather than two that can disagree.
 *
 * The three input-aware behaviours are no-ops in a track that holds links rather
 * than radios: `revealChecked` finds no `input:checked`, and `onFocusIn` returns
 * on anything that is not an INPUT — a focused <a> is scrolled into view by the
 * browser itself, which is only wrong for the picker because its radios are 1px
 * and clipped. So the nav bar needs no flag and no subclass.
 *
 * MARKUP CONTRACT — a caller renders:
 *   <im-option-scroller class="im-option-scroller">
 *     <button class="im-option-scroller__arrow im-option-scroller__arrow--prev"
 *             data-im-scroll="-1" hidden>…</button>
 *     <div class="im-option-scroller__track" data-im-scroll-track>…</div>
 *     <button class="im-option-scroller__arrow im-option-scroller__arrow--next"
 *             data-im-scroll="1" hidden>…</button>
 *   </im-option-scroller>
 * Both arrows are rendered `hidden`; this file is the only thing that unhides
 * them, so a page with JS blocked shows a scroller with no dead controls.
 *
 * The picker snippet renders on the PDP and in quick-view, so this file can be
 * asked for twice on one page; `customElements.get` is the guard.
 */
(function () {
  'use strict';

  var MOTION_SAFE_EDGE = 24; // px of the card to keep clear of the arrows

  function ImOptionScrollerClass() {
    return class ImOptionScroller extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-im-scroll-track]');
        if (!this.track) return;

        if (!this.bound) {
          this.bound = true;
          this.prev = this.querySelector('[data-im-scroll="-1"]');
          this.next = this.querySelector('[data-im-scroll="1"]');
          this.onRefresh = this.refresh.bind(this);

          this.track.addEventListener('scroll', this.queueRefresh.bind(this), { passive: true });
          this.track.addEventListener('focusin', this.onFocusIn.bind(this));
          this.addEventListener('click', this.onClick.bind(this));

          if (window.ResizeObserver) {
            this.observer = new ResizeObserver(this.onRefresh);
            this.observer.observe(this.track);
          } else {
            window.addEventListener('resize', this.onRefresh);
          }
        }

        /*
         * Every variant change replaces this element, so `connectedCallback` is
         * also the re-render hook: the freshly rendered track has a different set
         * of cards and a scroll position of 0, and the value that is now checked
         * may be off-screen to the right.
         */
        this.revealChecked();
        this.refresh();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      queueRefresh() {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(
          function () {
            this.frame = null;
            this.refresh();
          }.bind(this)
        );
      }

      refresh() {
        var track = this.track;
        if (!track) return;

        var max = track.scrollWidth - track.clientWidth;
        var overflows = max > 1;
        var at = track.scrollLeft;

        // A 1px tolerance: fractional widths and browser zoom both leave
        // scrollLeft a hair short of max, which would otherwise pin the arrow on
        // at the end of the track forever.
        toggle(this.prev, overflows && at > 1);
        toggle(this.next, overflows && at < max - 1);
      }

      onClick(event) {
        var button = event.target.closest ? event.target.closest('[data-im-scroll]') : null;
        if (!button || !this.contains(button)) return;

        event.preventDefault();
        var direction = button.getAttribute('data-im-scroll') === '-1' ? -1 : 1;
        var step = Math.max(120, Math.round(this.track.clientWidth * 0.8));
        this.track.scrollLeft += direction * step;
      }

      /*
       * The radios are 1px and clipped, and in a flex container an absolutely
       * positioned child sits at the container's content-box origin — so the
       * browser's own "scroll the focused element into view" scrolls to the start
       * of the track no matter which value has focus. The visible card is the
       * label, so that is what gets scrolled to. Native arrow-key movement through
       * the radio group is untouched; this only follows it.
       *
       * A track of links is left to the browser: an <a> is its own visible box, so
       * the native behaviour is already right, and `event.target.tagName` filters
       * it out here.
       */
      onFocusIn(event) {
        var input = event.target;
        if (!input || input.tagName !== 'INPUT') return;
        this.bring(input.nextElementSibling);
      }

      revealChecked() {
        var checked = this.track.querySelector('input:checked');
        if (checked) this.bring(checked.nextElementSibling);
      }

      /*
       * Deliberately not `scrollIntoView`: that is free to scroll the PAGE as well,
       * which on a PDP means the viewport jumping to the picker on every variant
       * change. Writing scrollLeft can only move this one container.
       */
      bring(card) {
        if (!card || !card.getBoundingClientRect) return;
        var track = this.track;
        var box = card.getBoundingClientRect();
        var frame = track.getBoundingClientRect();

        if (box.left < frame.left + MOTION_SAFE_EDGE) {
          track.scrollLeft += box.left - frame.left - MOTION_SAFE_EDGE;
        } else if (box.right > frame.right - MOTION_SAFE_EDGE) {
          track.scrollLeft += box.right - frame.right + MOTION_SAFE_EDGE;
        }
      }
    };
  }

  function toggle(element, show) {
    if (!element) return;
    if (show) element.removeAttribute('hidden');
    else element.setAttribute('hidden', '');
  }

  if (!customElements.get('im-option-scroller')) {
    customElements.define('im-option-scroller', ImOptionScrollerClass());
  }
})();
